import { ImageGallery } from '@little-emperors/components';

import Divider from 'blocks/Divider';
import SearchBar from 'blocks/search-bar';
import HotelFacilities from 'blocks/HotelFacilities';
import HotelInfo from 'blocks/HotelInfo/HotelInfo';
import JoinNow from 'blocks/JoinNow';
import GlobalLayout from 'blocks/Layouts/GlobalLayout';
import OurHotelsIn from 'blocks/OurHotelsIn/OurHotelsIn';
import {
  APRIL_FOOLS_FICTIONAL_HOTEL_ID,
  navigationPaths,
  PAGE_INVALIDATION_INTERVAL,
} from 'data/constants';
import type { NextPage, GetStaticProps, GetStaticPaths } from 'next';
import PageService from 'services/PageService/PageService';
import { getDescriptionFromContent } from 'utils/header';

import type { IGenericPage } from 'types/interfaces';
import type {
  HotelPublicType,
  OurHotelsInType,
  InspirationType,
} from 'types/types';

interface IHotelPage extends IGenericPage {
  hotel: HotelPublicType;
  similarHotels: OurHotelsInType[];
  inspirations: InspirationType[];
  initialSearchValue: string;
}

const GeneralHotelPage: NextPage<IHotelPage> = ({
  globalLayoutProps,
  hotel,
  similarHotels,
  inspirations,
  initialSearchValue,
}) => {
  return (
    <GlobalLayout {...globalLayoutProps}>
      <SearchBar
        inspirations={inspirations}
        initialSearchValue={initialSearchValue}
      />
      <div className="le-components">
        <ImageGallery
          images={hotel.images_gallery}
          videos={hotel.videos}
          defaultNoImageUrl="https://d13bre0qp8legl.cloudfront.net/default_big.jpg"
        />
      </div>
      <div className="container">
        <HotelInfo hotel={hotel} />
        <Divider />
        {hotel.hotel_information.length > 0 && (
          <HotelFacilities
            facilities={hotel.hotel_information}
            sustainability={hotel.sustainability_initiative}
          />
        )}
        <JoinNow />
      </div>
      <div className="outOfBounds">
        <OurHotelsIn
          location={hotel.location.split(',')[0]}
          hotels={similarHotels}
          maxPerPage={4}
        />
      </div>
    </GlobalLayout>
  );
};

export default GeneralHotelPage;

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const idHotel = typeof params?.idHotel === 'string' ? params.idHotel : '';

  if (idHotel === APRIL_FOOLS_FICTIONAL_HOTEL_ID) {
    return {
      redirect: {
        destination: navigationPaths.APRIL_FOOLS_RESET,
        permanent: false,
      },
    };
  }

  const data = await PageService.getHotelPublicPage(idHotel);
  const similarHotels = await PageService.getHotelsIn(idHotel);
  const inspirations = await PageService.getInspirationsPage();

  if (!data || Object.keys(data).length === 0) {
    return {
      notFound: true,
      revalidate: PAGE_INVALIDATION_INTERVAL,
    };
  }

  const processedDescription = getDescriptionFromContent(data.description);
  const genericPageProps = await PageService.getGenericPageProps({
    navPath: navigationPaths.OUR_HOTELS + `/${idHotel}`,
    title: `${data.name}`,
    description: processedDescription,
  });

  const sustainabilityRating = data.sustainability_rating;
  if (sustainabilityRating > 0) {
    data.hotel_information.push({
      icon_name: 'sustainability',
      title: 'Positive impact',
      description: [data.sustainability_initiative || ''],
      rating: sustainabilityRating,
    });
  }

  return {
    props: {
      ...genericPageProps,
      hotel: data,
      similarHotels: similarHotels,
      inspirations: inspirations.inspirations,
      initialSearchValue: data.name,
    },
    revalidate: PAGE_INVALIDATION_INTERVAL,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};
